// frontend/smartdocs-admin/src/components/settings/GeneralSettings.tsx
import React from 'react';
import { useSettings } from '@/context/SettingsContext';
import ToggleSwitch from '@shared/ToggleSwitch';
import SliderInput from '@shared/SliderInput';
import ColorPicker from '@shared/ColorPicker';
import SettingsPage from '@/pages/SettingsPage';
import Card from "@shared/Card";
import Input from "@shared/Input";

const GeneralSettings: React.FC = () => {
  const { settings, updateSetting, loading } = useSettings();

  // Conditional rendering to prevent accessing settings.General before data is loaded
  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <p className="text-lg text-gray-700 dark:text-gray-300">Loading settings...</p>
      </div>
    );
  }

  const generalSettings = settings.General || {};

  const handleUpdate = (key: string, value: any) => {
    updateSetting('General', key, value);
  };

  return (
    <SettingsPage>
      <Card>
        <div className="space-y-6">
      {/*<h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">General System Settings</h2>*/}

      {/* System Name */}
      <div>
        <label htmlFor="systemName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">System Name</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">The display name for the application.</p>
        <Input
          type="text"
          id="systemName"
          className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={generalSettings?.systemName || 'SmartDocs AI'}
          onChange={(e) => handleUpdate('systemName', e.target.value)}
          disabled={loading}
        />
      </div>

      {/* Dark Mode Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <label htmlFor="darkMode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Enable Dark Mode</label>
          <p className="text-sm text-gray-500 dark:text-gray-400">Toggle between light and dark themes for the UI.</p>
        </div>
        <ToggleSwitch
          id="darkMode"
          label=""
          checked={generalSettings?.darkMode || false}
          onChange={(e) => handleUpdate('darkMode', e.target.checked)}
          disabled={loading}
        />
      </div>

      {/* Items Per Page Slider */}
      <div>
        <label htmlFor="itemsPerPage" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Items Per Page</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Number of items to display per page in lists and tables.</p>
        <SliderInput
          id="itemsPerPage"
          label=""
          min={5}
          max={50}
          step={5}
          value={generalSettings?.itemsPerPage || 10}
          onChange={(value) => handleUpdate('itemsPerPage', value)}
          disabled={loading}
        />
      </div>

      {/* Primary Color Picker */}
      <div>
        <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary Accent Color</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Choose the primary accent color for the application UI.</p>
        <ColorPicker
          id="primaryColor"
          label=""
          value={generalSettings?.primaryColor || '#3B82F6'} // Default to blue-500
          onChange={(color) => handleUpdate('primaryColor', color)}
          disabled={loading}
        />
      </div>

      {/* Date Format Selector */}
      <div>
        <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Format</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Select the preferred date display format.</p>
        <select
          id="dateFormat"
          className="mt-1 block w-full h-12 rounded-md border-1 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={generalSettings?.dateFormat || 'MM/DD/YYYY'}
          onChange={(e) => handleUpdate('dateFormat', e.target.value)}
          disabled={loading}
        >
          <option value="MM/DD/YYYY">MM/DD/YYYY (e.g., 01/25/2024)</option>
          <option value="DD/MM/YYYY">DD/MM/YYYY (e.g., 25/01/2024)</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD (e.g., 2024-01-25)</option>
          <option value="MMM DD, YYYY">MMM DD, YYYY (e.g., Jan 25, 2024)</option>
        </select>
      </div>
    </div>
      </Card>
    </SettingsPage>
  );
};

export default GeneralSettings;
